import React, { useEffect, useState } from "react";
import { Flame, TrendingUp, Award, CalendarCheck, GraduationCap, HeartHandshake, Users, Check, X } from "lucide-react";
import api from "../../lib/api";
import { PageTitle, Card, Badge, Loading, fmtDate } from "./ui";
import { useAuth } from "../../context/AuthContext";

function Stat({ icon: Icon, label, value, suffix, accent }) {
  return (
    <Card>
      <div className="flex items-center gap-2 text-gold-deep">
        <Icon size={18} />
        <span className="font-sans text-xs font-semibold uppercase tracking-widest">{label}</span>
      </div>
      <p className={`mt-3 font-display text-4xl font-semibold ${accent || "text-sapphire-deep"}`}>
        {value}{suffix && <span className="ml-1 font-sans text-base text-ink/50">{suffix}</span>}
      </p>
    </Card>
  );
}

export default function MyProfile() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/portal/my-engagement").then((r) => setMe(r.data)).catch(() => setMe({ error: true }));
  }, []);

  if (!me) return <Loading />;
  if (me.error) return <Card><p className="text-ink/70">Unable to load your profile.</p></Card>;

  return (
    <div data-testid="profile-page">
      <PageTitle eyebrow="Your Standing" title={me.name || user?.name}
        sub={`${me.rank}${me.year_initiated ? ` · Initiated ${me.year_initiated}` : ""}`} />

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={TrendingUp} label="Attendance" value={me.attendance_rate} suffix="%" />
        <Stat icon={Flame} label="Current Streak" value={me.current_streak} suffix="mtgs" accent="text-garnet" />
        <Stat icon={Award} label="Engagement" value={me.engagement_score} suffix="/100" />
        <Card>
          <div className="flex items-center gap-2 text-gold-deep">
            <CalendarCheck size={18} />
            <span className="font-sans text-xs font-semibold uppercase tracking-widest">Standing</span>
          </div>
          <div className="mt-4"><Badge>{me.status}</Badge></div>
          <p className="mt-3 font-sans text-sm text-ink/60">
            {me.attended} of {me.total} recent meetings · longest streak {me.longest_streak}
          </p>
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Contributions */}
        <Card className="lg:col-span-1">
          <h3 className="font-display text-xl font-semibold text-sapphire-deep">Contributions</h3>
          <ul className="mt-5 space-y-4 font-sans text-sm">
            <li className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-ink/70"><GraduationCap size={16} className="text-gold-deep" /> Lodge of Instruction</span>
              <span className="font-display text-lg text-sapphire-deep">{me.loi_sessions}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-ink/70"><HeartHandshake size={16} className="text-gold-deep" /> Charity events</span>
              <span className="font-display text-lg text-sapphire-deep">{me.charity_events}</span>
            </li>
            <li>
              <span className="flex items-center gap-2 text-ink/70"><Users size={16} className="text-gold-deep" /> Committees</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {(me.committees || []).length ? me.committees.map((c) => (
                  <span key={c} className="rounded-full bg-sapphire-deep/8 px-3 py-1 text-xs font-medium text-sapphire-deep">{c}</span>
                )) : <span className="text-xs italic text-ink/40">None recorded</span>}
              </div>
            </li>
          </ul>
        </Card>

        {/* Attendance timeline */}
        <Card className="lg:col-span-2">
          <h3 className="font-display text-xl font-semibold text-sapphire-deep">Recent Attendance</h3>
          <p className="mt-1 font-sans text-sm text-ink/55">Your presence at the last {me.total} meetings.</p>
          {me.timeline?.length ? (
            <div className="mt-5 flex flex-wrap gap-3" data-testid="profile-timeline">
              {me.timeline.map((t, i) => (
                <div key={i} className="flex w-28 flex-col items-center gap-2 rounded-xl border border-sapphire-deep/10 p-3 text-center">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-full ${t.present ? "bg-emerald/15 text-emerald" : "bg-garnet/12 text-garnet"}`}>
                    {t.present ? <Check size={18} /> : <X size={18} />}
                  </span>
                  <span className="font-sans text-[11px] leading-tight text-ink/60">{fmtDate(t.date).replace(/ \d{4}$/, "")}</span>
                  <span className="font-sans text-[10px] uppercase tracking-wide text-ink/40">{t.title.split(" ")[0]}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-5 font-sans text-sm italic text-ink/50">No meetings recorded yet.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
